import React from 'react';
import i18n from "i18next";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import Accordion from "../components/Accordion";
import MultiRangeSlider from "../components/multiRangeSlider/MultiRangeSlider";
import { PopupForm } from "../components/MailRequest";
import { FiShoppingCart, FiUsers, FiDatabase, FiFilter } from 'react-icons/fi';
import './OrderContainer.css';

export default class OrderContainer extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            response: '[]',
            status_code: 200,
            isLoading: true,
            // Filter lists from backend
            sex_list: [],
            type_list: [],
            organization_list: [],
            collection_list: [],
            // Checked states
            sex_checked: [],
            type_checked: [],
            organization_checked: [],
            collection_checked: [],
            age_settings_checked: [true, true],
            // Age range
            age: [0, 120],
            age_min: 0,
            age_max: 120,
            // Available items based on current selection (from /filters/ endpoint)
            availableOrganizations: new Set(),
            availableCollections: new Set(),
            availableTypes: new Set(),
            availableSex: new Set()
        };
    }

    componentDidMount() {
        const headers = { 'Access-Control-Request-Method': 'GET' };
        const params = new URLSearchParams({ full: 'true' });
        this.handleFetch(`${process.env.REACT_APP_BACK_END}/assays/?${params.toString()}`, { headers }, true);
    }

    // Fetch available filter options from /filters/ endpoint
    // Full many-to-many dependencies: all filters affect each other
    fetchAvailableFilters = () => {
        const selectedOrg = this.state.organization_list
            .filter((_, i) => this.state.organization_checked[i]);
        const selectedCol = this.state.collection_list
            .filter((_, i) => this.state.collection_checked[i]);
        const selectedType = this.state.type_list
            .filter((_, i) => this.state.type_checked[i]);
        const selectedSex = this.state.sex_list
            .filter((_, i) => this.state.sex_checked[i]);

        const params = new URLSearchParams({
            age_min: this.state.age[0],
            age_max: this.state.age[1],
            age_na: this.state.age_settings_checked[0],
            age_nb: this.state.age_settings_checked[1]
        });

        // If any main filter is empty, all dependent filters should be empty (no results possible)
        if (selectedOrg.length === 0 || selectedCol.length === 0 ||
            selectedType.length === 0 || selectedSex.length === 0) {
            this.setState({
                availableOrganizations: selectedOrg.length === 0 ? new Set(this.state.organization_list) : new Set(),
                availableCollections: selectedCol.length === 0 ? new Set(this.state.collection_list) : new Set(),
                availableTypes: selectedType.length === 0 ? new Set(this.state.type_list) : new Set(),
                availableSex: selectedSex.length === 0 ? new Set(this.state.sex_list) : new Set()
            });
            return;
        }

        if (selectedOrg.length) params.set('organization', selectedOrg.join(','));
        if (selectedCol.length) params.set('collection', selectedCol.join(','));
        if (selectedType.length) params.set('type', selectedType.join(','));
        if (selectedSex.length) params.set('sex', selectedSex.join(','));

        fetch(`${process.env.REACT_APP_BACK_END}/filters/?${params}`)
            .then(r => r.json())
            .then(data => {
                this.setState({
                    availableOrganizations: new Set(data.organizations || []),
                    availableCollections: new Set(data.collections || []),
                    availableTypes: new Set(data.types || []),
                    availableSex: new Set(data.sex || [])
                });
            })
            .catch(error => {
                console.error('Error fetching available filters:', error);
            });
    }

    handleFetch = (url, options, isInitial = false) => {
        this.setState({ isLoading: true });
        fetch(url, options)
            .then(response => {
                this.setState({ status_code: response.status });
                return response.text();
            })
            .then(data => {
                const parsed = JSON.parse(data);

                // Check if response is an error string (like "merge_zero", "assays_zero")
                const ERRORS = ["assays_zero", "patients_zero", "organizations_zero", "collections_zero", "merge_zero"];
                if (typeof parsed === 'string' && ERRORS.includes(parsed)) {
                    this.setState({ response: parsed, isLoading: false });
                    return;
                }

                const selectors = parsed.selectors || {};

                // Initialize filter lists
                const sex_list = selectors.sex_list || [];
                const type_list = selectors.type_list || [];
                const organization_list = selectors.organization_list || [];
                const collection_list = selectors.collection_list || [];

                // Only reset checked states on initial load
                const newState = {
                    response: data,
                    isLoading: false,
                    sex_list,
                    type_list,
                    organization_list,
                    collection_list
                };

                if (isInitial) {
                    newState.sex_checked = Array(sex_list.length).fill(true);
                    newState.type_checked = Array(type_list.length).fill(true);
                    newState.organization_checked = Array(organization_list.length).fill(true);
                    newState.collection_checked = Array(collection_list.length).fill(true);

                    // Initially all items are available
                    newState.availableOrganizations = new Set(organization_list);
                    newState.availableCollections = new Set(collection_list);
                    newState.availableTypes = new Set(type_list);
                    newState.availableSex = new Set(sex_list);
                }

                this.setState(newState);
            })
            .catch(error => this.setState({ status_code: 500, response: error.message, isLoading: false }));
    }

    applyFilters = () => {
        const headers = { 'Content-Type': 'application/json' };
        const { availableOrganizations, availableCollections, availableTypes, availableSex } = this.state;

        // Only include items that are checked AND available
        const selectedOrg = this.state.organization_list.filter((org, i) =>
            this.state.organization_checked[i] && availableOrganizations.has(org)
        );
        const selectedCol = this.state.collection_list.filter((col, i) =>
            this.state.collection_checked[i] && availableCollections.has(col)
        );
        const selectedType = this.state.type_list.filter((type, i) =>
            this.state.type_checked[i] && availableTypes.has(type)
        );
        const selectedSex = this.state.sex_list.filter((sex, i) =>
            this.state.sex_checked[i] && availableSex.has(sex)
        );

        // If any required filter is empty, show "no results" without making request
        if (selectedOrg.length === 0 || selectedCol.length === 0 ||
            selectedType.length === 0 || selectedSex.length === 0) {
            this.setState({
                response: 'assays_zero',
                status_code: 200,
                isLoading: false
            });
            return;
        }

        const params = new URLSearchParams({
            type: selectedType.join(','),
            collection: selectedCol.join(','),
            organization: selectedOrg.join(','),
            sex: selectedSex.join(','),
            age_min: this.state.age[0],
            age_max: this.state.age[1],
            age_na: this.state.age_settings_checked[0],
            age_nb: this.state.age_settings_checked[1]
        });

        this.handleFetch(`${process.env.REACT_APP_BACK_END}/assays/?${params.toString()}`, { headers });
    }

    handleCheckboxChange = (listName, index) => {
        const checkedName = listName.replace('_list', '_checked');
        const newChecked = [...this.state[checkedName]];
        newChecked[index] = !newChecked[index];
        this.setState({ [checkedName]: newChecked }, () => {
            this.fetchAvailableFilters();
        });
    }

    handleSelectAll = (listName, selectAll) => {
        const checkedName = listName.replace('_list', '_checked');
        const list = this.state[listName];
        this.setState({ [checkedName]: Array(list.length).fill(selectAll) }, () => {
            this.fetchAvailableFilters();
        });
    }

    handleAgeSettingChange = (index) => {
        const newSettings = [...this.state.age_settings_checked];
        newSettings[index] = !newSettings[index];
        this.setState({ age_settings_checked: newSettings }, () => {
            this.fetchAvailableFilters();
        });
    }

    handleChangeMinAge = (val) => {
        const age = [...this.state.age];
        age[0] = val;
        this.setState({ age }, () => {
            this.fetchAvailableFilters();
        });
    }

    handleChangeMaxAge = (val) => {
        const age = [...this.state.age];
        age[1] = val;
        this.setState({ age }, () => {
            this.fetchAvailableFilters();
        });
    }

    getFilterQuery = () => {
        const selectedSex = this.state.sex_list
            .filter((_, i) => this.state.sex_checked[i]);
        const selectedType = this.state.type_list
            .filter((_, i) => this.state.type_checked[i]);
        const selectedOrg = this.state.organization_list
            .filter((_, i) => this.state.organization_checked[i]);
        const selectedCol = this.state.collection_list
            .filter((_, i) => this.state.collection_checked[i]);

        const ageSettings = [];
        if (this.state.age_settings_checked[0]) ageSettings.push('age_na');
        if (this.state.age_settings_checked[1]) ageSettings.push('age_nb');

        return {
            sex: selectedSex,
            age: this.state.age,
            age_settings: ageSettings,
            type: selectedType,
            organization: selectedOrg,
            collection: selectedCol
        };
    }

    renderFilterSection = (listName, labelKey) => {
        const list = this.state[listName];
        const checkedName = listName.replace('_list', '_checked');
        const checked = this.state[checkedName];
        const prefix = listName.replace('_list', '_');
        const allSelected = checked.every(c => c);
        const { availableOrganizations, availableCollections, availableTypes, availableSex } = this.state;

        // Determine which set to check for availability
        let availableSet = null;
        if (listName === 'organization_list') {
            availableSet = availableOrganizations;
        } else if (listName === 'collection_list') {
            availableSet = availableCollections;
        } else if (listName === 'type_list') {
            availableSet = availableTypes;
        } else if (listName === 'sex_list') {
            availableSet = availableSex;
        }

        return (
            <div className="order-filter-section">
                <div className="order-filter-header">
                    <span>{i18n.t(labelKey)}</span>
                    <button
                        type="button"
                        className="order-select-all-btn"
                        onClick={() => this.handleSelectAll(listName, !allSelected)}
                    >
                        {allSelected ? i18n.t('clear_all_button') : i18n.t('select_all_button')}
                    </button>
                </div>
                <div className="order-filter-options">
                    {list.map((item, idx) => {
                        const isAvailable = !availableSet || availableSet.has(item);
                        return (
                            <label
                                key={item}
                                className={`order-checkbox-label ${!isAvailable ? 'order-checkbox-disabled' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked[idx] || false}
                                    onChange={() => this.handleCheckboxChange(listName, idx)}
                                />
                                <span>{i18n.t(`${prefix}${item}`) || item}</span>
                            </label>
                        );
                    })}
                </div>
            </div>
        );
    }

    renderAggregatedData = () => {
        const { response, status_code } = this.state;
        const ERRORS = ["assays_zero", "patients_zero", "organizations_zero", "collections_zero", "merge_zero"];

        if (ERRORS.includes(response)) {
            return (
                <div className="order-results">
                    <div className="order-summary-cards">
                        <div className="order-summary-card">
                            <FiDatabase size={24} />
                            <div className="order-summary-value">0</div>
                            <div className="order-summary-label">{i18n.t('found_assays')}</div>
                        </div>
                    </div>
                    <p className="empty-state">
                        {i18n.t("no_results_found")}
                    </p>
                </div>
            );
        }

        try {
            const parsed = JSON.parse(response);
            const tableJson = parsed.table_json ? JSON.parse(parsed.table_json) : [];
            const assayN = parsed.assay_n || 0;

            return (
                <div className="order-results">
                    <div className="order-summary-cards">
                        <div className="order-summary-card">
                            <FiDatabase size={24} />
                            <div className="order-summary-value">{assayN}</div>
                            <div className="order-summary-label">{i18n.t('found_assays')}</div>
                        </div>
                        <div className="order-summary-card">
                            <FiUsers size={24} />
                            <div className="order-summary-value">{tableJson.length}</div>
                            <div className="order-summary-label">{i18n.t('filter_organization')}</div>
                        </div>
                    </div>

                    {tableJson.length > 0 && (
                        <Accordion className="order-accordion">
                            {tableJson.map((orgData, orgIdx) => {
                                const orgName = i18n.t(`organization_${orgData.organization}`);
                                const orgTotal = orgData.n || 0;
                                const collections = orgData.collection || [];

                                return (
                                    <Accordion.Item
                                        key={orgIdx}
                                        title={orgName}
                                        badge={orgTotal}
                                        defaultOpen={orgIdx === 0}
                                    >
                                        {collections.length > 0 ? (
                                            <Accordion>
                                                {collections.map((colData, colIdx) => {
                                                    const colName = i18n.t(`collection_${colData.collection}`);
                                                    const colTotal = colData.n || 0;
                                                    const types = colData.type || [];

                                                    return (
                                                        <Accordion.Item
                                                            key={colIdx}
                                                            title={colName}
                                                            badge={colTotal}
                                                        >
                                                            {types.length > 0 ? (
                                                                <table className="accordion-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>{i18n.t('filter_type')}</th>
                                                                            <th>{i18n.t('n')}</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {types.map((typeData, typeIdx) => (
                                                                            <tr key={typeIdx}>
                                                                                <td>{i18n.t(`type_${typeData.type}`)}</td>
                                                                                <td>{typeData.n}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : (
                                                                <p className="no-data-text">{i18n.t('assays_zero')}</p>
                                                            )}
                                                        </Accordion.Item>
                                                    );
                                                })}
                                            </Accordion>
                                        ) : (
                                            <p className="no-data-text">{i18n.t('collection_zero')}</p>
                                        )}
                                    </Accordion.Item>
                                );
                            })}
                        </Accordion>
                    )}

                    {assayN > 0 && (
                        <PopupForm
                            object_type="order"
                            query={this.getFilterQuery()}
                        />
                    )}
                </div>
            );
        } catch (e) {
            return <ErrorMessage type="error" message="error_unknown" />;
        }
    }


    render() {
        const { isLoading, status_code } = this.state;

        return (
            <div className="order-container">
                <h1 className="order-title">
                    <FiShoppingCart size={28} />
                    {i18n.t('order_title')}
                </h1>
                <p className="order-description">{i18n.t('order_description')}</p>

                <div className="order-layout">
                    {/* Filters Panel */}
                    <div className="order-filters-panel">
                        <div className="order-filters-header">
                            <FiFilter size={18} />
                            <span>{i18n.t('order_filters_title')}</span>
                        </div>

                        <div className="order-filters-grid">
                            {this.renderFilterSection('organization_list', 'filter_organization')}
                            {this.renderFilterSection('collection_list', 'filter_collection')}
                            {this.renderFilterSection('type_list', 'filter_type')}
                            {this.renderFilterSection('sex_list', 'filter_sex')}

                            {/* Age Settings */}
                            <div className="order-filter-section">
                                <div className="order-filter-header">
                                    <span>{i18n.t('filter_age')}</span>
                                </div>
                                <div className="order-filter-options">
                                    <label className="order-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={this.state.age_settings_checked[0]}
                                            onChange={() => this.handleAgeSettingChange(0)}
                                        />
                                        <span>{i18n.t('age_settings_age_na')}</span>
                                    </label>
                                    <label className="order-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={this.state.age_settings_checked[1]}
                                            onChange={() => this.handleAgeSettingChange(1)}
                                        />
                                        <span>{i18n.t('age_settings_age_nb')}</span>
                                    </label>
                                </div>
                                <div className="order-age-slider">
                                    <MultiRangeSlider
                                        min={this.state.age_min}
                                        max={this.state.age_max}
                                        onChange={() => {}}
                                        changeMin={this.handleChangeMinAge}
                                        changeMax={this.handleChangeMaxAge}
                                    />
                                </div>
                            </div>
                        </div>

                        <button className="order-apply-btn" onClick={this.applyFilters}>
                            {i18n.t('request_button')}
                        </button>
                    </div>

                    {/* Results Panel */}
                    <div className="order-results-panel">
                        {isLoading ? (
                            <LoadingSpinner message="loading_data" size="md" />
                        ) : status_code >= 500 ? (
                            <ErrorMessage
                                type="error"
                                message="error_server"
                                onRetry={() => this.componentDidMount()}
                            />
                        ) : (
                            this.renderAggregatedData()
                        )}
                    </div>
                </div>

            </div>
        );
    }
}
